/** Widget kartasi: Apple to'ri o'lchamlari, xato bo'lsa faqat shu karta "yiqiladi", tahrirlashda sudraladi. */
import { Component, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Minus, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import { ContextMenu, type MenuEntry } from "@/components/ui/context-menu";
import type { WidgetDef, WidgetItem, WidgetSize } from "./types";

/** Panel ichi 300px, 2 ustun, oraliq 12px: S -- kvadrat, M -- to'liq en, L -- ikki qator balandlikda */
export const SIZE_CLASS: Record<WidgetSize, string> = {
  s: "col-span-1 h-[144px]",
  m: "col-span-2 h-[144px]",
  l: "col-span-2 h-[300px]",
};

export function WidgetCard({ size, className, children }: { size: WidgetSize; className?: string; children: ReactNode }) {
  return <div className={cn("relative overflow-hidden rounded-[22px] border border-line bg-surface shadow-sm", SIZE_CLASS[size], className)}>{children}</div>;
}

class Boundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function Failed() {
  const { t } = useTranslation("widgets");
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 p-3 text-center text-[12px] text-muted">
      <TriangleAlert size={16} strokeWidth={1.75} />
      {t("failed")}
    </div>
  );
}

/** Ko'rinishni xato chegarasi ichida chizadi (galereya oldindan ko'rinishi ham shuni ishlatadi) */
export function WidgetBody({ def, item, me }: { def: WidgetDef; item: WidgetItem; me: Me }) {
  const View = def.View;
  return (
    <Boundary fallback={<Failed />}>
      <View item={item} me={me} />
    </Boundary>
  );
}

export function SortableWidget({
  item,
  def,
  me,
  editing,
  menu,
  onOpen,
  onRemove,
}: {
  item: WidgetItem;
  def: WidgetDef;
  me: Me;
  editing: boolean;
  menu: () => MenuEntry[];
  onOpen: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation("widgets");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !editing });
  const name = t(def.title);
  return (
    <ContextMenu items={menu}>
      <div
        ref={setNodeRef}
        style={{ transform: CSS.Translate.toString(transform), transition }}
        className={cn(SIZE_CLASS[item.size], "relative touch-manipulation", isDragging && "z-20")}
        {...(editing ? { ...attributes, ...listeners, "aria-label": t("editWidget", { name }), onClick: onOpen } : {})}
      >
        <WidgetCard
          size={item.size}
          className={cn("h-full transition-[transform,box-shadow] duration-200 ease-out", editing && "cursor-grab", isDragging && "scale-[1.03] cursor-grabbing shadow-xl")}
        >
          <div className={cn("h-full", editing && "pointer-events-none")}>
            <WidgetBody def={def} item={item} me={me} />
          </div>
        </WidgetCard>
        {editing && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label={t("removeNamed", { name })}
            title={t("removeNamed", { name })}
            className="widget-badge absolute -top-1.5 -left-1.5 z-10 grid h-6 w-6 place-items-center rounded-full bg-border text-ink shadow-sm ring-2 ring-sidebar transition-colors hover:bg-danger hover:text-surface"
          >
            <Minus size={14} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </ContextMenu>
  );
}
